import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StatusPill } from '@ui-kit';
import { consumerGuideUrl } from '../lib/consumerGuideLink';

interface PathRow {
  key: 'nexus' | 'remake' | 'zip';
  name: string;
  situation: string;
  prerequisite: string;
  effort: string;
  comingSoon: boolean;
}

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-3)',
  borderBottom: '2px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  verticalAlign: 'top',
};

/**
 * §6.3 섹션 1 — 가져다쓰기 3경로 비교표.
 * ㉮ Nexus(#629 eraf-bom)·㉯ 소스 리메이크는 consumer-guide 딥링크로 활성.
 * ㉰ zip 은 #641 로 완결 — comingSoon 해제, 상태 셀은 동일 페이지 큐레이션 섹션
 * (`#gsg-bundle` · CurationBundlePicker)으로 스크롤하는 앵커 링크.
 */
export function AdoptionPathTable() {
  const { t } = useTranslation();
  const guideUrl = consumerGuideUrl();

  const rows: PathRow[] = [
    {
      key: 'nexus',
      name: t('gettingStartedGuide.paths.nexus.name'),
      situation: t('gettingStartedGuide.paths.nexus.situation'),
      prerequisite: t('gettingStartedGuide.paths.nexus.prerequisite'),
      effort: t('gettingStartedGuide.paths.nexus.effort'),
      comingSoon: false,
    },
    {
      key: 'remake',
      name: t('gettingStartedGuide.paths.remake.name'),
      situation: t('gettingStartedGuide.paths.remake.situation'),
      prerequisite: t('gettingStartedGuide.paths.remake.prerequisite'),
      effort: t('gettingStartedGuide.paths.remake.effort'),
      comingSoon: false,
    },
    {
      key: 'zip',
      name: t('gettingStartedGuide.paths.zip.name'),
      situation: t('gettingStartedGuide.paths.zip.situation'),
      prerequisite: t('gettingStartedGuide.paths.zip.prerequisite'),
      effort: t('gettingStartedGuide.paths.zip.effort'),
      comingSoon: false,
    },
  ];

  return (
    <section aria-labelledby="gsg-paths-heading">
      <h2
        id="gsg-paths-heading"
        style={{ margin: '0 0 var(--spacing-3) 0', fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}
      >
        {t('gettingStartedGuide.paths.heading')}
      </h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.paths.table.col.path')}</th>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.paths.table.col.situation')}</th>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.paths.table.col.prerequisite')}</th>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.paths.table.col.effort')}</th>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.paths.table.col.status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} data-testid={`adoption-path-row-${row.key}`}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{row.name}</td>
                <td style={tdStyle}>{row.situation}</td>
                <td style={tdStyle}>{row.prerequisite}</td>
                <td style={tdStyle}>{row.effort}</td>
                <td style={tdStyle}>
                  {row.key === 'zip' ? (
                    // #641: ㉰ 는 별도 문서 링크 대신 동일 페이지 큐레이션 섹션으로 스크롤
                    <a href="#gsg-bundle" style={{ color: 'var(--color-info)' }}>
                      {t('gettingStartedGuide.paths.status.buildBundle')}
                    </a>
                  ) : row.comingSoon ? (
                    <StatusPill tone="warning">{t('gettingStartedGuide.paths.status.comingSoon')}</StatusPill>
                  ) : (
                    <Link to={guideUrl} style={{ color: 'var(--color-info)' }}>
                      {t('gettingStartedGuide.paths.status.viewGuide')}
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
